// src/components/ResultsManager.jsx
import { useEffect, useState, useCallback } from "react";
import {
    Container,
    Typography,
    Box,
    Button,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    CircularProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import { authHeader } from "../auth";

// 🌐 Domaine public utilisé pour générer le lien complet à copier
const PUBLIC_BASE_URL = "https://321-timing.ch";

// ⚠️ À protéger côté routing : n'afficher ce composant que si
// l'utilisateur connecté a le rôle "chronometreur" (ou "admin").
export default function ResultsManager() {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const fetchDocuments = useCallback(() => {
        setLoading(true);
        fetch("/api/results")
            .then((res) => res.json())
            .then((data) => setDocuments(data))
            .catch(() => showSnackbar("Erreur lors du chargement des documents.", "error"))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const showSnackbar = (message, severity = "success") =>
        setSnackbar({ open: true, message, severity });

    // ⬆️ Upload d'un nouveau document
    const handleUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("document", file);

        setUploading(true);
        fetch("/api/results/upload", {
            method: "POST",
            headers: authHeader(),
            body: formData,
        })
            .then((res) => {
                if (!res.ok) throw new Error();
                return res.json();
            })
            .then(() => {
                showSnackbar("Document ajouté avec succès.");
                fetchDocuments();
            })
            .catch(() => showSnackbar("Échec de l'upload.", "error"))
            .finally(() => {
                setUploading(false);
                e.target.value = ""; // reset l'input pour pouvoir réuploader le même fichier
            });
    };

    // 🗑️ Suppression d'un document
    const handleDelete = (filename) => {
        if (!window.confirm(`Supprimer "${filename}" ? Cette action est irréversible.`)) return;

        fetch(`/api/results/${encodeURIComponent(filename)}`, {
            method: "DELETE",
            headers: authHeader(),
        })
            .then((res) => {
                if (!res.ok) throw new Error();
                showSnackbar("Document supprimé.");
                fetchDocuments();
            })
            .catch(() => showSnackbar("Échec de la suppression.", "error"));
    };

    // 📋 Copie le lien complet dans le presse-papier
    const handleCopyLink = (url) => {
        const fullUrl = `${PUBLIC_BASE_URL}${url}`;
        navigator.clipboard.writeText(fullUrl);
        showSnackbar("Lien copié dans le presse-papier.");
    };

    return (
        <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                <Typography variant="h4">Documents (listes de départ, résultats, etc.)</Typography>
                <Button
                    variant="contained"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    disabled={uploading}
                >
                    {uploading ? "Envoi..." : "Ajouter un document"}
                    <input type="file" hidden onChange={handleUpload} />
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : documents.length === 0 ? (
                <Typography color="text.secondary">Aucun document pour le moment.</Typography>
            ) : (
                <List>
                    {documents.map((doc) => (
                        <ListItem
                            key={doc.filename}
                            sx={{ border: "1px solid #eee", borderRadius: 2, mb: 1 }}
                            secondaryAction={
                                <>
                                    <Tooltip title="Copier le lien">
                                        <IconButton onClick={() => handleCopyLink(doc.url)}>
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Supprimer">
                                        <IconButton color="error" onClick={() => handleDelete(doc.filename)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            }
                        >
                            <DescriptionIcon sx={{ mr: 2, color: "primary.main" }} />
                            <ListItemText
                                primary={doc.filename}
                                secondary={`${PUBLIC_BASE_URL}${doc.url}`}
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
}